import type {
    AgentMessageFragmentClarifyingQuestions,
    AgentMessageFragmentNextStepsProposal,
    Message,
} from "@/hooks/use-stream-conversation";

export type PendingInteractiveFragment =
    | AgentMessageFragmentClarifyingQuestions
    | AgentMessageFragmentNextStepsProposal;

export function extractSpecFilePath(messages: Message[]): string | null {
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.type !== "agent") continue;

        for (let j = message.fragments.length - 1; j >= 0; j--) {
            const fragment = message.fragments[j];
            if (
                fragment.type === "fileModification" &&
                (fragment.filePath === "spec.md" || fragment.filePath.endsWith("/spec.md"))
            ) {
                return fragment.filePath;
            }
        }
    }
    return null;
}

export function extractPendingInteractiveBlock(messages: Message[]): PendingInteractiveFragment | null {
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.type !== "agent") continue;

        for (let j = message.fragments.length - 1; j >= 0; j--) {
            const fragment = message.fragments[j];
            if (fragment.type === "clarifyingQuestions" || fragment.type === "nextStepsProposal") {
                const hasAnswer = messages.slice(i + 1).some((m) => m.type === "user");
                if (hasAnswer) {
                    return null;
                }
                // Next-step proposals are ephemeral — stale once the agent
                // sends another message after the one containing the proposal.
                if (fragment.type === "nextStepsProposal") {
                    const hasLaterAgentMessage = messages.slice(i + 1).some((m) => m.type === "agent");
                    if (hasLaterAgentMessage) {
                        return null;
                    }
                }
                return fragment;
            }
        }
    }
    return null;
}

export function isPendingInteractiveFragment(
    fragment: { type: string; id: string },
    pendingBlock: PendingInteractiveFragment | null,
): boolean {
    if (!pendingBlock) return false;
    return fragment.id === pendingBlock.id;
}

export function isAnsweredInteractiveFragment(
    fragment: { type: string },
    messageIndex: number,
    messages: Message[],
): boolean {
    if (fragment.type !== "clarifyingQuestions" && fragment.type !== "nextStepsProposal") {
        return false;
    }
    const hasUserAnswer = messages.slice(messageIndex + 1).some((m) => m.type === "user");
    if (hasUserAnswer) {
        return true;
    }
    // Next-step proposals are ephemeral — hide them once the agent sends
    // another message, since the proposal is no longer current.
    if (fragment.type === "nextStepsProposal") {
        return messages.slice(messageIndex + 1).some((m) => m.type === "agent");
    }
    return false;
}
